/** Bill of Material — BOM.md.
 *
 * ONE BOM PER ITEM, IN REVISIONS. GHERP had one BOM per item and overwrote it;
 * its own lock against editing a released BOM ("Exists in DRN... Cannot
 * Edit.. Please use DCR!!") was commented out and the table it needed was
 * never created. This screen is what GHERP intended: a released revision is
 * read-only, and a change is a new revision — usually a copy of the current
 * one, with the difference made on the copy.
 *
 * EVERY RULE IS THE DATABASE'S (migration 046). This screen offers only what
 * makes sense — no edit controls on a released revision, no inactive part in
 * the picker — and when the database refuses anyway, its words are shown as
 * they were written: "Item is already present at position A1", "…would contain
 * itself". A check here is a courtesy; the rule is there.
 *
 * RIGHTS decide what is SHOWN (RIGHTS_MODEL.md 6.1): New BOM and a new
 * revision need `add`, changing a draft needs `modify`, Release needs its own
 * `release` — somebody drafts, somebody answerable releases — and Export
 * needs `print`. The server checks every one of them regardless.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import { saveFile, type ExportedFile } from '@/lib/download'
import { SearchSelect, type Option } from '@/components/SearchSelect'
import { fmtDMY } from './MasterScreen'
import { useObjectRights } from '@/lib/rights'

// ---------------------------------------------------------------------------
// Shapes, as the API returns them (backend/functions/masters/bom.py)
// ---------------------------------------------------------------------------

type State = 'Draft' | 'Scheduled' | 'Current' | 'Superseded' | 'Cancelled'

interface BomRow {
  item_id: string; item_name: string; item_category: string
  current_revision: number | null; current_from: string | null
  scheduled_revision: number | null; draft_revision: number | null
  revisions: number
}
interface Makeable {
  item_id: string; item_name: string; item_category: string; has_bom: boolean
}
interface Revision {
  bom_id: string; revision: number; state: State; effective_from: string | null
  remarks: string | null; copied_from_revision: number | null
  released_by: string | null; released_at: string | null
  cancel_reason: string | null; lines: number; version: number
}
interface Line {
  line_no: number; item_id: string; item_name: string; item_category: string
  uom: string; qty: string | number; position_mark: string | null
  is_optional: boolean; remarks: string | null; status: 'Active' | 'Void'
  version: number; item_active: boolean; weight_kg: string | number | null
  has_own_bom: boolean
}
interface RevisionDetail {
  header: Revision & { item_id: string; item_name: string }
  lines: Line[]
  totals: { lines: number; optional: number; weight_kg: string | number
            unweighed: string[]; inactive: string[] }
}
interface StructureRow {
  level: number; item_id: string; item_name: string; item_category: string
  uom: string; qty_each: string | number; qty_total: string | number
  weight_kg: string | number | null; is_optional: boolean
  position_mark: string | null; path: number[]; item_active: boolean
  child_revision: number | null; exploded: boolean
}
interface FlatRow {
  item_id: string; item_name: string; uom: string; qty_total: string | number
  weight_kg: string | number | null; places: number; optional_only: boolean
  exploded: boolean; item_active: boolean
}
interface Structure {
  header: { bom_id: string; item_id: string; item_name: string
            revision: number; state: State; effective_from: string | null }
  as_of: string
  rows: StructureRow[]
  flat: FlatRow[]
  totals: { lines: number; parts: number; levels: number; optional: number
            weight_kg: string | number; unweighed: string[]
            inactive: string[]; truncated: boolean }
}
interface Component {
  item_id: string; item_name: string; item_category: string; uom: string
  tagged: boolean
}

const CATEGORY: Record<string, string> = {
  Item: 'Item', SubAssembly: 'Sub-Assembly', Assembly: 'Assembly',
  MainAssembly: 'Main Assembly',
}
const today = () => new Date().toISOString().slice(0, 10)

/** The day after a date. A revision takes effect AFTER the one it replaces -
 *  never on the same day, which the database refuses and a unique index makes
 *  impossible besides (migration 046, bom_header_one_per_date). */
function dayAfter(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + 1)
  return d.toISOString().slice(0, 10)
}

/** A quantity as the unit allows — 4, 12.5, never 4.000. */
function fmtQty(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—'
  const n = Number(v)
  return Number.isFinite(n)
    ? n.toLocaleString('en-IN', { maximumFractionDigits: 3 })
    : String(v)
}

function message(e: unknown, fallback: string): string {
  return e instanceof ApiError || e instanceof Error ? e.message : fallback
}

/** Re-fetch when the tab regains focus — but never while somebody is typing,
 *  which would interrupt them. UI_TASTE.md, Data Freshness. */
function useRefetchOnFocus(load: () => void) {
  useEffect(() => {
    function onFocus() {
      const el = document.activeElement
      if (el && ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)) return
      load()
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [load])
}

// ---------------------------------------------------------------------------

export function BomScreen() {
  const [open, setOpen] = useState<string | null>(null)
  return open
    ? <BomDetail key={open} itemId={open} onBack={() => setOpen(null)}
                 onOpen={setOpen} />
    : <BomList onOpen={setOpen} />
}

// ---------------------------------------------------------------------------
// The list
// ---------------------------------------------------------------------------

function BomList({ onOpen }: { onOpen: (itemId: string) => void }) {
  const [rows, setRows] = useState<BomRow[] | null>(null)
  const [search, setSearch] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [exporting, setExporting] = useState(false)
  const rights = useObjectRights('masters', 'bom')

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ rows: BomRow[] }>('/masters/bom')
      setRows(r.rows ?? [])
      setProblem(null)
    } catch (e) {
      // Keep the last good list rather than blanking the screen.
      setProblem(message(e, 'The list could not be loaded.'))
    }
  }, [])
  useEffect(() => { void load() }, [load])
  useRefetchOnFocus(() => { void load() })

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (rows ?? []).filter(r => !q
      || r.item_id.toLowerCase().includes(q)
      || r.item_name.toLowerCase().includes(q))
  }, [rows, search])

  async function exportList() {
    setExporting(true)
    try {
      saveFile(await api.get<ExportedFile>('/masters/bom/export'))
    } catch (e) {
      setProblem(message(e, 'The export could not be produced.'))
    } finally { setExporting(false) }
  }

  return (
    <>
      <h1 className="page-title">Bill of Material</h1>
      <p className="page-subtitle">
        The parts list for each item you make — in revisions. A released
        revision is never edited; a change is a new revision.
      </p>

      {problem && <div className="form-errors">{problem}</div>}

      {creating && (
        <NewBomForm
          existing={rows ?? []}
          onCancel={() => setCreating(false)}
          onCreated={itemId => { setCreating(false); onOpen(itemId) }}
        />
      )}

      <div className="card">
        <div className="toolbar">
          <input type="search" placeholder="Search by item code or name…"
                 value={search} onChange={e => setSearch(e.target.value)}
                 style={{ minWidth: 260 }} aria-label="Search bills of material" />
          <div className="grow" />
          {rights.can('print') && (
            <button className="btn btn-secondary" onClick={() => void exportList()}
                    disabled={exporting || !rows?.length}>
              {exporting ? 'Preparing…' : 'Export to Excel'}
            </button>
          )}
          {rights.can('add') && (
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              New BOM
            </button>
          )}
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>Item</th><th style={{ width: 130 }}>Category</th>
                <th style={{ width: 190 }}>Current</th>
                <th style={{ width: 110 }}>Scheduled</th>
                <th style={{ width: 90 }}>Draft</th>
                <th className="num" style={{ width: 90 }}>Revisions</th>
              </tr>
            </thead>
            <tbody>
              {rows === null && (
                <tr><td colSpan={6} className="loading">Loading…</td></tr>
              )}
              {rows !== null && shown.length === 0 && (
                <tr><td colSpan={6} className="empty">
                  {search
                    ? `Nothing matches “${search}”.`
                    : 'No bills of material yet. Start one with New BOM — for ' +
                      'a sub-assembly, assembly or main assembly.'}
                </td></tr>
              )}
              {shown.map(r => (
                <tr key={r.item_id} className="row-openable"
                    title="Double-click to open"
                    onDoubleClick={() => onOpen(r.item_id)}>
                  <td>
                    <button className="link-btn" onClick={() => onOpen(r.item_id)}>
                      {r.item_id}
                    </button>{' '}— {r.item_name}
                  </td>
                  <td>{CATEGORY[r.item_category] ?? r.item_category}</td>
                  <td>
                    {r.current_revision
                      ? <>Rev {r.current_revision} <span className="row-muted">
                          · from {fmtDMY(r.current_from)}</span></>
                      : <span className="row-muted">none in force</span>}
                  </td>
                  <td>{r.scheduled_revision ? `Rev ${r.scheduled_revision}` : '—'}</td>
                  <td>{r.draft_revision ? `Rev ${r.draft_revision}` : '—'}</td>
                  <td className="num">{r.revisions}</td>
                </tr>
              ))}
              {shown.length > 0 && (
                <tr className="total-row">
                  <td colSpan={6} className="num">
                    Total: {shown.length} bill{shown.length === 1 ? '' : 's'} of material
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Starting a BOM — empty, or Copy From (GHERP's own, and how a new trailer
// variant's BOM is actually made)
// ---------------------------------------------------------------------------

/** The revision to copy from an item: what is in force, else what is coming,
 *  else the draft, else the latest ever released. Never a cancelled one. */
async function copyableRevision(itemId: string): Promise<Revision | null> {
  const r = await api.get<{ revisions: Revision[] }>(`/masters/bom/item/${itemId}`)
  const order: State[] = ['Current', 'Scheduled', 'Draft', 'Superseded']
  for (const s of order) {
    const hit = r.revisions.find(v => v.state === s)
    if (hit) return hit
  }
  return null
}

function NewBomForm({ existing, onCancel, onCreated }: {
  existing: BomRow[]
  onCancel: () => void
  onCreated: (itemId: string) => void
}) {
  const [makeable, setMakeable] = useState<Makeable[] | null>(null)
  const [itemId, setItemId] = useState('')
  const [copyFrom, setCopyFrom] = useState('')
  const [remarks, setRemarks] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  useEffect(() => {
    api.get<{ rows: Makeable[] }>('/masters/bom/makeable')
      .then(r => setMakeable(r.rows ?? []))
      .catch(e => { setMakeable([]); setProblem(message(e, 'Items could not be loaded.')) })
  }, [])

  const chosen = makeable?.find(m => m.item_id === itemId)
  const openDraft = existing.find(r => r.item_id === itemId && r.draft_revision)

  // An item that already has a BOM starts its next revision as a copy of
  // itself — the ordinary way a BOM changes. Offered, not forced.
  useEffect(() => {
    if (chosen?.has_bom) setCopyFrom(chosen.item_id)
  }, [chosen])

  const itemOptions: Option[] = (makeable ?? []).map(m => ({
    value: m.item_id,
    label: `${m.item_id} — ${m.item_name} (${CATEGORY[m.item_category]})`,
    group: m.has_bom ? 'Already has a BOM — a new revision' : 'No BOM yet',
  })).sort((a, b) => (a.group ?? '').localeCompare(b.group ?? ''))

  const copyOptions: Option[] = existing.map(r => ({
    value: r.item_id, label: `${r.item_id} — ${r.item_name}` }))

  async function save() {
    setBusy(true); setProblem(null)
    try {
      let copy_from: string | undefined
      if (copyFrom) {
        const src = await copyableRevision(copyFrom)
        if (!src) throw new Error('That item has no revision to copy from.')
        copy_from = src.bom_id
      }
      await api.create('/masters/bom/rev', { item_id: itemId, copy_from, remarks })
      onCreated(itemId)
    } catch (e) {
      setProblem(message(e, 'The BOM could not be started.'))
    } finally { setBusy(false) }
  }

  return (
    <div className="card form-card">
      <div className="card-header"><h2>New BOM</h2></div>
      {problem && <div className="form-errors">{problem}</div>}
      <div className="form-grid">
        <div className="form-field">
          <label>Item <span className="req">*</span></label>
          <SearchSelect value={itemId} onChange={setItemId} options={itemOptions}
                        emptyLabel="— choose the item this BOM makes —"
                        disabled={makeable === null} />
          <div className="form-help">
            {makeable === null ? 'Loading items…'
              : makeable.length === 0
                ? 'No active sub-assembly, assembly or main assembly exists. ' +
                  'Only something you make has a BOM — create one on the Items screen.'
                : 'Sub-assemblies and above. A plain item is bought, so it has no BOM.'}
          </div>
          {openDraft && (
            <div className="hint-warn">
              Revision {openDraft.draft_revision} of this BOM is already a
              draft. Finish or cancel it first — two drafts at once would mean
              one release silently discarding the other’s work.
            </div>
          )}
        </div>

        <div className="form-field">
          <label>Copy From</label>
          <SearchSelect value={copyFrom} onChange={setCopyFrom} options={copyOptions}
                        emptyLabel="— start empty —" />
          <div className="form-help">
            {copyFrom
              ? 'Copies the lines of its current revision. Change them on the draft.'
              : existing.length === 0
                ? 'Nothing to copy yet — this will be the first BOM.'
                : 'Start from a similar item’s BOM, or empty.'}
          </div>
        </div>

        <div className="form-field span-2">
          <label>Remarks</label>
          <textarea value={remarks} onChange={e => setRemarks(e.target.value)}
                    placeholder="Why this revision — a customer change, a design correction…" />
        </div>
      </div>

      <p className="form-note">
        Saved as a <strong>Draft</strong>. It becomes what the item is made of
        only when somebody with the right releases it.
      </p>
      <div className="form-actions">
        <button className="btn btn-primary" onClick={() => void save()}
                disabled={busy || !itemId || !!openDraft}>
          {busy ? 'Starting…' : 'Start Draft'}
        </button>
        <button className="btn btn-secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// One item's BOM
// ---------------------------------------------------------------------------

type Tab = 'lines' | 'structure' | 'revisions' | 'where-used' | 'compare'

function BomDetail({ itemId, onBack, onOpen }: {
  itemId: string; onBack: () => void; onOpen: (itemId: string) => void
}) {
  const [item, setItem] = useState<{ item_name: string; item_category: string } | null>(null)
  const [revisions, setRevisions] = useState<Revision[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [detail, setDetail] = useState<RevisionDetail | null>(null)
  const [tab, setTab] = useState<Tab>('lines')
  const [problem, setProblem] = useState<string | null>(null)
  const [panel, setPanel] = useState<null | 'release' | 'cancel'>(null)
  const rights = useObjectRights('masters', 'bom')

  const loadRevisions = useCallback(async (keep?: string) => {
    try {
      const r = await api.get<{ item: { item_name: string; item_category: string }
                                revisions: Revision[] }>(`/masters/bom/item/${itemId}`)
      setItem(r.item)
      setRevisions(r.revisions)
      // Open on what somebody is most likely here for: the draft being
      // worked on, else what is in force.
      const pick = keep && r.revisions.some(v => v.bom_id === keep) ? keep
        : (r.revisions.find(v => v.state === 'Draft')
           ?? r.revisions.find(v => v.state === 'Current')
           ?? r.revisions.find(v => v.state === 'Scheduled')
           ?? r.revisions[0])?.bom_id ?? null
      setSelected(pick)
      setProblem(null)
    } catch (e) {
      setProblem(message(e, 'This BOM could not be loaded.'))
    }
  }, [itemId])

  const loadDetail = useCallback(async () => {
    if (!selected) { setDetail(null); return }
    try {
      setDetail(await api.get<RevisionDetail>(`/masters/bom/rev/${selected}`))
    } catch (e) {
      setProblem(message(e, 'The revision could not be loaded.'))
    }
  }, [selected])

  useEffect(() => { void loadRevisions() }, [loadRevisions])
  useEffect(() => { void loadDetail() }, [loadDetail])
  useRefetchOnFocus(() => { void loadRevisions(selected ?? undefined) })

  const rev = revisions.find(v => v.bom_id === selected) ?? null
  const isDraft = rev?.state === 'Draft'
  // The released revision with the latest effective date - the one a draft
  // must take effect AFTER. The database looks at exactly this row.
  const previous = revisions
    .filter(v => v.effective_from && v.state !== 'Cancelled')
    .sort((a, b) => (a.effective_from! < b.effective_from! ? 1 : -1))[0] ?? null
  // A draft is changed only with `modify`; without it, it is read.
  const canChange = isDraft && rights.can('modify')
  const hasDraft = revisions.some(v => v.state === 'Draft')

  /** After any change: re-fetch, never trust the local copy. UI_TASTE.md. */
  async function refresh(keep?: string) {
    await loadRevisions(keep ?? selected ?? undefined)
    await loadDetail()
  }

  async function newRevision() {
    if (!rev) return
    try {
      const r = await api.create<{ bom_id: string }>('/masters/bom/rev',
        { item_id: itemId, copy_from: rev.bom_id })
      setTab('lines')
      await refresh(r.bom_id)
    } catch (e) { setProblem(message(e, 'The new revision could not be started.')) }
  }

  async function exportLines() {
    if (!selected) return
    try { saveFile(await api.get<ExportedFile>(`/masters/bom/rev/${selected}/export`)) }
    catch (e) { setProblem(message(e, 'The export could not be produced.')) }
  }

  const revOptions: Option[] = revisions.map(v => ({
    value: v.bom_id, label: `Rev ${v.revision} — ${v.state}` }))

  return (
    <>
      <p className="page-subtitle" style={{ marginBottom: 8 }}>
        <button className="link-btn" onClick={onBack}>Bill of Material</button>
        {' / '}{itemId}
      </p>

      {problem && <div className="form-errors">{problem}</div>}

      <div className="card detail">
        <div className="card-header detail-head">
          <div>
            <div className="detail-kind">
              Bill of Material · {item ? CATEGORY[item.item_category] : ''}
            </div>
            <h2 className="detail-title">{item?.item_name ?? '…'}</h2>
            <div className="detail-code">{itemId}</div>
          </div>
          <div className="detail-actions">
            {isDraft && rights.can('release') && (
              <button className="btn btn-primary" onClick={() => setPanel('release')}>
                Release…
              </button>
            )}
            {canChange && (
              <button className="btn btn-secondary" onClick={() => setPanel('cancel')}>
                Cancel Draft…
              </button>
            )}
            {rev && !isDraft && rev.state !== 'Cancelled' && rights.can('add') && (
              <button className="btn btn-secondary" disabled={hasDraft}
                      title={hasDraft ? 'A draft revision is already open' : undefined}
                      onClick={() => void newRevision()}>
                New Revision from Rev {rev.revision}
              </button>
            )}
            {rights.can('print') && (
              <button className="btn btn-secondary" onClick={() => void exportLines()}
                      disabled={!detail?.lines.length}>Export to Excel</button>
            )}
            <button className="btn btn-secondary" onClick={onBack}>Close</button>
          </div>
        </div>

        {revisions.length === 0 && item && (
          <div className="empty">This item has no BOM revision yet.</div>
        )}

        {rev && (
          <dl className="detail-grid">
            <div className="detail-item">
              <dt>Revision</dt>
              <dd style={{ maxWidth: 240 }}>
                <SearchSelect value={selected ?? ''} options={revOptions}
                              onChange={v => { if (v) { setSelected(v); setPanel(null) } }}
                              emptyLabel="— choose a revision —" />
              </dd>
            </div>
            <div className="detail-item">
              <dt>State</dt>
              <dd><span className={`badge badge-${rev.state.toLowerCase()}`}>{rev.state}</span></dd>
            </div>
            <div className="detail-item">
              <dt>Effective From</dt><dd>{fmtDMY(rev.effective_from)}</dd>
            </div>
            <div className="detail-item">
              <dt>Released</dt>
              <dd>{rev.released_at
                ? `${fmtDMY(rev.released_at)}${rev.released_by ? ` · ${rev.released_by}` : ''}`
                : '—'}</dd>
            </div>
            <div className="detail-item">
              <dt>Copied From</dt>
              <dd>{rev.copied_from_revision ? `Rev ${rev.copied_from_revision}` : '—'}</dd>
            </div>
            {rev.state === 'Cancelled' && (
              <div className="detail-item">
                <dt>Cancelled Because</dt><dd>{rev.cancel_reason}</dd>
              </div>
            )}
            <div className="detail-item span-2">
              <dt>Remarks</dt><dd>{rev.remarks || '—'}</dd>
            </div>
          </dl>
        )}
      </div>

      {panel === 'release' && rev && detail && (
        <ReleasePanel rev={rev} detail={detail} previous={previous}
                      onDone={async () => { setPanel(null); await refresh() }}
                      onClose={() => setPanel(null)} />
      )}
      {panel === 'cancel' && rev && (
        <CancelPanel rev={rev}
                     onDone={async () => { setPanel(null); await refresh() }}
                     onClose={() => setPanel(null)} />
      )}

      <div className="tabs" role="tablist">
        {([['lines', 'Lines'], ['structure', 'Structure'],
           ['revisions', `Revisions (${revisions.length})`],
           ['where-used', 'Where Used'], ['compare', 'Compare']] as [Tab, string][])
          .map(([k, label]) => (
            <button key={k} role="tab" aria-selected={tab === k}
                    className={`tab${tab === k ? ' active' : ''}`}
                    onClick={() => setTab(k)}>{label}</button>
          ))}
      </div>

      {tab === 'lines' && detail && (
        <LinesTab detail={detail} editable={canChange} onOpen={onOpen}
                  onChanged={() => void refresh()} />
      )}
      {tab === 'structure' && selected && (
        <StructureTab bomId={selected} canPrint={rights.can('print')} onOpen={onOpen} />
      )}
      {tab === 'revisions' && (
        <RevisionsTab revisions={revisions} selected={selected}
                      onSelect={id => { setSelected(id); setTab('lines') }} />
      )}
      {tab === 'where-used' && <WhereUsedTab itemId={itemId} onOpen={onOpen} />}
      {tab === 'compare' && (
        <CompareTab revisions={revisions} selected={selected} />
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Release and cancel — deliberate acts, each in its own panel
// ---------------------------------------------------------------------------

function ReleasePanel({ rev, detail, previous, onDone, onClose }: {
  rev: Revision; detail: RevisionDetail; previous: Revision | null
  onDone: () => Promise<void>; onClose: () => void
}) {
  // THE EARLIEST DATE THIS REVISION CAN TAKE EFFECT. Today, unless a revision
  // is already effective today or later - then the day after that one.
  //
  // 13 September 2026: this panel offered TODAY and refused anything earlier,
  // while the current revision had been released the same morning. The date
  // it handed you was the one date the database was certain to refuse, so
  // Release looked alive and did nothing. Work the date out, do not assume it.
  const earliest = previous?.effective_from
    ? (dayAfter(previous.effective_from) > today()
        ? dayAfter(previous.effective_from) : today())
    : today()
  const [date, setDate] = useState(earliest)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const { totals } = detail

  // What the database will refuse, said before anyone asks. It still
  // decides — this is the courtesy in front of the rule.
  const blockers: string[] = []
  if (totals.lines === 0) blockers.push('It has no lines. A BOM cannot be released empty.')
  if (totals.inactive.length) blockers.push(
    `Inactive part${totals.inactive.length > 1 ? 's' : ''} ${totals.inactive.join(', ')} ` +
    '— replace before releasing.')

  async function release() {
    setBusy(true); setProblem(null)
    try {
      await api.create(`/masters/bom/rev/${rev.bom_id}/release`, { effective_from: date })
      await onDone()
    } catch (e) { setProblem(message(e, 'The revision could not be released.')) }
    finally { setBusy(false) }
  }

  return (
    <div className="card form-card">
      <div className="card-header"><h2>Release Rev {rev.revision}</h2></div>
      {problem && <div className="form-errors">{problem}</div>}
      <p className="form-note">
        From the date below, revision {rev.revision} is what this item is made
        of. A released revision is <strong>never edited</strong> — a later change
        is a new revision.
      </p>
      {blockers.map(b => <div key={b} className="hint-warn">{b}</div>)}
      <div className="form-grid">
        <div className="form-field">
          <label htmlFor="bom-effective-from">
            Effective From <span className="req">*</span>
          </label>
          {/* Not today or later - EARLIEST or later. A date in the past
              would change what was current on a day already gone, and a date
              on or before the current revision's would leave two revisions
              claiming the same day. The database refuses both. */}
          <input id="bom-effective-from" type="date" value={date} min={earliest}
                 onChange={e => setDate(e.target.value)} />
          <div className="form-help">
            {previous?.effective_from && earliest > today()
              ? `Revision ${previous.revision} takes effect on `
                + `${fmtDMY(previous.effective_from)}, so this one can take `
                + `effect from ${fmtDMY(earliest)} — or any later date for a `
                + 'planned change.'
              : 'Today, or a later date for a planned change'}
          </div>
        </div>
      </div>
      <div className="form-actions">
        <button className="btn btn-primary" onClick={() => void release()}
                disabled={busy || blockers.length > 0 || !date || date < earliest}>
          {busy ? 'Releasing…' : `Release Rev ${rev.revision}`}
        </button>
        <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Close</button>
      </div>
    </div>
  )
}

function CancelPanel({ rev, onDone, onClose }: {
  rev: Revision; onDone: () => Promise<void>; onClose: () => void
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  async function cancel() {
    setBusy(true); setProblem(null)
    try {
      await api.create(`/masters/bom/rev/${rev.bom_id}/cancel`, { reason })
      await onDone()
    } catch (e) { setProblem(message(e, 'The draft could not be cancelled.')) }
    finally { setBusy(false) }
  }

  return (
    <div className="card form-card">
      <div className="card-header"><h2>Cancel Draft Rev {rev.revision}</h2></div>
      {problem && <div className="form-errors">{problem}</div>}
      <p className="form-note">
        The draft is kept, marked cancelled, with your reason. Its revision
        number is not reused.
      </p>
      <div className="form-grid">
        <div className="form-field span-2">
          <label>Reason <span className="req">*</span></label>
          <textarea value={reason} onChange={e => setReason(e.target.value)}
                    placeholder="Why this draft is being abandoned" />
        </div>
      </div>
      <div className="form-actions">
        <button className="btn btn-primary" onClick={() => void cancel()}
                disabled={busy || !reason.trim()}>
          {busy ? 'Cancelling…' : 'Cancel Draft'}
        </button>
        <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Close</button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Lines
// ---------------------------------------------------------------------------

interface LineDraft {
  item_id: string; qty: string; position_mark: string
  is_optional: boolean; remarks: string
}
const blankLine: LineDraft = {
  item_id: '', qty: '', position_mark: '', is_optional: false, remarks: '' }

function LinesTab({ detail, editable, onOpen, onChanged }: {
  detail: RevisionDetail; editable: boolean
  onOpen: (itemId: string) => void; onChanged: () => void
}) {
  const bomId = detail.header.bom_id
  const [components, setComponents] = useState<Component[] | null>(null)
  const [adding, setAdding] = useState<LineDraft>(blankLine)
  const [editing, setEditing] = useState<{ line_no: number; d: LineDraft } | null>(null)
  const [showVoid, setShowVoid] = useState(false)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  useEffect(() => {
    if (!editable) return
    api.get<{ rows: Component[] }>(`/masters/bom/rev/${bomId}/components`)
      .then(r => setComponents(r.rows ?? []))
      .catch(e => { setComponents([]); setProblem(message(e, 'Parts could not be loaded.')) })
  }, [bomId, editable])

  // ITEM_MASTER.md §10.4 — tagged parts first, then EVERYTHING ELSE under a
  // heading. Hiding untagged parts would make people create duplicates.
  const partOptions: Option[] = useMemo(() => (components ?? []).map(c => ({
    value: c.item_id,
    label: `${c.item_id} — ${c.item_name} (${c.uom})`,
    group: c.tagged ? 'Tagged to this item’s models' : 'Everything else',
  })), [components])

  const lines = detail.lines.filter(l => showVoid || l.status === 'Active')
  const voided = detail.lines.length - detail.lines.filter(l => l.status === 'Active').length

  async function run(fn: () => Promise<unknown>, fail: string) {
    setBusy(true); setProblem(null)
    try { await fn(); onChanged(); return true }
    catch (e) { setProblem(message(e, fail)); return false }
    finally { setBusy(false) }
  }

  async function add() {
    const ok = await run(() => api.create(`/masters/bom/rev/${bomId}/lines`, adding),
                         'The line could not be added.')
    if (ok) setAdding({ ...blankLine })
  }

  async function saveEdit(l: Line) {
    if (!editing) return
    const ok = await run(() => api.update(`/masters/bom/rev/${bomId}/lines/${l.line_no}`,
                                          { ...editing.d, version: l.version }),
                         'The line could not be saved.')
    if (ok) setEditing(null)
  }

  async function voidLine(l: Line) {
    if (!window.confirm(`Take line ${l.line_no} (${l.item_name}) out of this draft?\n\n` +
                        'It is voided, not deleted — it stays on record.')) return
    await run(() => api.create(`/masters/bom/rev/${bomId}/lines/${l.line_no}/void`,
                               { version: l.version }),
              'The line could not be voided.')
  }

  const onEnter = (fn: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); fn() }
  }

  const t = detail.totals
  return (
    <div className="card">
      {problem && <div className="form-errors">{problem}</div>}
      {!editable && (
        <div className="banner">
          {detail.header.state === 'Cancelled'
            ? 'This draft was cancelled. It is kept for the record and cannot be changed.'
            : detail.header.state === 'Draft'
              // Not a state reason — a right. Said plainly, once.
              ? 'You can read this draft but not change it.'
              : `Revision ${detail.header.revision} is ${detail.header.state.toLowerCase()} ` +
                'and cannot be edited. To change it, start a new revision from it.'}
        </div>
      )}
      {t.inactive.length > 0 && (
        <div className="banner banner-warning">
          Inactive part{t.inactive.length > 1 ? 's' : ''}: {t.inactive.join(', ')}.
          {editable ? ' Replace before releasing — a release will be refused.' : ''}
        </div>
      )}

      <div className="toolbar">
        <span className="row-muted">
          {t.lines} line{t.lines === 1 ? '' : 's'}
          {t.optional ? ` · ${t.optional} optional` : ''}
        </span>
        <div className="grow" />
        {voided > 0 && (
          <label className="check-row">
            <input type="checkbox" checked={showVoid}
                   onChange={e => setShowVoid(e.target.checked)} />
            Show {voided} voided
          </label>
        )}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="grid">
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th><th>Part</th>
              <th style={{ width: 100 }}>Position</th>
              <th className="num" style={{ width: 90 }}>Qty</th>
              <th style={{ width: 60 }}>UOM</th>
              <th className="num" style={{ width: 100 }}>Weight kg</th>
              <th style={{ width: 80 }}>Optional</th>
              <th>Remarks</th>
              {editable && <th style={{ width: 110 }}></th>}
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 && (
              <tr><td colSpan={editable ? 9 : 8} className="empty">
                {editable ? 'No lines yet — add the first part below.'
                          : 'This revision has no lines.'}
              </td></tr>
            )}
            {lines.map(l => editing?.line_no === l.line_no ? (
              <tr key={l.line_no}>
                <td>{l.line_no}</td>
                <td><SearchSelect value={editing.d.item_id} options={partOptions}
                      onChange={v => setEditing({ ...editing, d: { ...editing.d, item_id: v } })} /></td>
                <td><input className="cell" value={editing.d.position_mark}
                      onChange={e => setEditing({ ...editing, d: { ...editing.d, position_mark: e.target.value } })}
                      onKeyDown={onEnter(() => void saveEdit(l))} /></td>
                <td><input className="cell num" inputMode="decimal" value={editing.d.qty}
                      onChange={e => setEditing({ ...editing, d: { ...editing.d, qty: e.target.value } })}
                      onKeyDown={onEnter(() => void saveEdit(l))} /></td>
                <td>{l.uom}</td><td></td>
                <td><input type="checkbox" checked={editing.d.is_optional}
                      onChange={e => setEditing({ ...editing, d: { ...editing.d, is_optional: e.target.checked } })} /></td>
                <td><input className="cell" value={editing.d.remarks}
                      onChange={e => setEditing({ ...editing, d: { ...editing.d, remarks: e.target.value } })}
                      onKeyDown={onEnter(() => void saveEdit(l))} /></td>
                <td><div className="row-actions">
                  <button className="link-btn" disabled={busy} onClick={() => void saveEdit(l)}>Save</button>
                  <button className="link-btn" disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
                </div></td>
              </tr>
            ) : (
              <tr key={l.line_no} className={l.status === 'Void' ? 'row-muted' : undefined}>
                <td>{l.line_no}</td>
                <td>
                  {/* A part that is itself made opens its own BOM — every
                      reference is a door. UI_TASTE.md. */}
                  {l.has_own_bom
                    ? <button className="link-btn" onClick={() => onOpen(l.item_id)}
                              title="Open this part’s own BOM">{l.item_id}</button>
                    : l.item_id}{' '}— {l.item_name}
                  {!l.item_active && <span className="badge badge-inactive" style={{ marginLeft: 6 }}>Inactive</span>}
                  {l.status === 'Void' && <span className="badge badge-cancelled" style={{ marginLeft: 6 }}>Void</span>}
                </td>
                <td>{l.position_mark ?? '—'}</td>
                <td className="num">{fmtQty(l.qty)}</td>
                <td>{l.uom}</td>
                <td className="num">{l.weight_kg == null ? '—' : fmtQty(l.weight_kg)}</td>
                <td>{l.is_optional ? 'Optional' : ''}</td>
                <td>{l.remarks ?? ''}</td>
                {editable && (
                  <td>{l.status === 'Active' && (
                    <div className="row-actions">
                      <button className="link-btn" disabled={busy}
                              onClick={() => setEditing({ line_no: l.line_no, d: {
                                item_id: l.item_id, qty: String(Number(l.qty)),
                                position_mark: l.position_mark ?? '',
                                is_optional: l.is_optional, remarks: l.remarks ?? '' } })}>
                        Edit
                      </button>
                      <button className="link-btn" disabled={busy}
                              onClick={() => void voidLine(l)}>Void</button>
                    </div>
                  )}</td>
                )}
              </tr>
            ))}

            {editable && (
              <tr>
                <td className="row-muted">+</td>
                <td>
                  <SearchSelect value={adding.item_id} options={partOptions}
                                disabled={components === null}
                                emptyLabel={components === null ? 'Loading parts…' : '— choose a part —'}
                                onChange={v => setAdding({ ...adding, item_id: v })} />
                </td>
                <td><input className="cell" placeholder="e.g. A1" value={adding.position_mark}
                      onChange={e => setAdding({ ...adding, position_mark: e.target.value })}
                      onKeyDown={onEnter(() => void add())} /></td>
                <td><input className="cell num" inputMode="decimal" placeholder="Qty"
                      value={adding.qty}
                      onChange={e => setAdding({ ...adding, qty: e.target.value })}
                      onKeyDown={onEnter(() => void add())} /></td>
                <td className="row-muted">
                  {components?.find(c => c.item_id === adding.item_id)?.uom ?? ''}
                </td>
                <td></td>
                <td><input type="checkbox" checked={adding.is_optional}
                      aria-label="Optional"
                      onChange={e => setAdding({ ...adding, is_optional: e.target.checked })} /></td>
                <td><input className="cell" placeholder="Remarks" value={adding.remarks}
                      onChange={e => setAdding({ ...adding, remarks: e.target.value })}
                      onKeyDown={onEnter(() => void add())} /></td>
                <td>
                  <button className="btn btn-primary btn-sm" onClick={() => void add()}
                          disabled={busy || !adding.item_id || !adding.qty}>
                    {busy ? 'Adding…' : 'Add'}
                  </button>
                </td>
              </tr>
            )}

            {t.lines > 0 && (
              <tr className="total-row">
                <td colSpan={5} className="num">Weight</td>
                <td className="num">{fmtQty(t.weight_kg)}</td>
                <td colSpan={editable ? 3 : 2}></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editable && components !== null && components.length === 0 && (
        <p className="form-help">
          No active parts to choose from. Parts are created on the Items screen.
        </p>
      )}
      {t.lines > 0 && (
        <p className="form-help">
          {t.optional > 0 && 'Optional lines are left out of the weight — the ' +
            'customer chooses one of each group, so only one is built. '}
          {t.unweighed.length > 0 && `${t.unweighed.length} part${t.unweighed.length > 1 ? 's have' : ' has'} ` +
            `no weight and ${t.unweighed.length > 1 ? 'are' : 'is'} not in the total: ` +
            `${t.unweighed.join(', ')}. Set the weight per unit on the Items screen.`}
        </p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Structure — the whole tree, exploded
// ---------------------------------------------------------------------------

/** GHERP kept this on a SEPARATE screen ("Bill Of Material — View"), reached
 *  through a different menu, showing an indented list it could only print or
 *  export. It is a tab here: the same BOM, seen all the way down.
 *
 *  Two views of one answer — the TREE, which is how an engineer reads it, and
 *  the FLAT list, every part with its total quantity, which is what a buyer
 *  takes to the market. */
function StructureTab({ bomId, canPrint, onOpen }: {
  bomId: string; canPrint: boolean; onOpen: (itemId: string) => void
}) {
  const [data, setData] = useState<Structure | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [view, setView] = useState<'tree' | 'flat'>('tree')
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [asOf, setAsOf] = useState<string>('')

  const load = useCallback(async () => {
    try {
      const q = asOf ? { as_of: asOf } : undefined
      setData(await api.get<Structure>(`/masters/bom/rev/${bomId}/structure`, q))
      setProblem(null)
    } catch (e) {
      setProblem(message(e, 'The structure could not be worked out.'))
    }
  }, [bomId, asOf])
  useEffect(() => { setData(null); setCollapsed(new Set()); void load() }, [load])

  const key = (path: number[]) => path.join('.')

  /** A row is a parent when the row after it sits one level deeper — the
   *  rows arrive in tree order, so no second query is needed. */
  const parents = useMemo(() => {
    const set = new Set<string>()
    const rows = data?.rows ?? []
    rows.forEach((r, i) => {
      if (rows[i + 1] && rows[i + 1].level > r.level) set.add(r.path.join('.'))
    })
    return set
  }, [data])

  /** Hidden when any assembly above it is closed. */
  const visible = useMemo(() => (data?.rows ?? []).filter(r => {
    for (let n = 1; n < r.path.length; n++) {
      if (collapsed.has(r.path.slice(0, n).join('.'))) return false
    }
    return true
  }), [data, collapsed])

  function toggle(path: number[]) {
    const k = key(path)
    setCollapsed(prev => {
      const next = new Set(prev)
      if (!next.delete(k)) next.add(k)
      return next
    })
  }
  const collapseAll = () => setCollapsed(new Set(parents))
  const expandAll = () => setCollapsed(new Set())

  async function exportStructure() {
    try {
      saveFile(await api.get<ExportedFile>(
        `/masters/bom/rev/${bomId}/structure/export`,
        asOf ? { as_of: asOf } : undefined))
    } catch (e) { setProblem(message(e, 'The export could not be produced.')) }
  }

  if (problem) return <div className="form-errors">{problem}</div>
  if (!data) return <p className="form-help">Working out the structure…</p>

  const t = data.totals
  if (t.lines === 0) {
    return (
      <p className="form-help">
        This revision has no lines yet, so there is nothing below it.
      </p>
    )
  }

  return (
    <div className="tab-panel">
      <div className="toolbar">
        <div className="toolbar-left">
          <button className={`btn btn-sm${view === 'tree' ? ' btn-primary' : ''}`}
                  onClick={() => setView('tree')}>Tree</button>
          <button className={`btn btn-sm${view === 'flat' ? ' btn-primary' : ''}`}
                  onClick={() => setView('flat')}>Flat list</button>
          {view === 'tree' && parents.size > 0 && (
            <>
              <button className="link-btn" onClick={expandAll}>Expand all</button>
              <button className="link-btn" onClick={collapseAll}>Collapse all</button>
            </>
          )}
        </div>
        <div className="toolbar-right">
          {/* Each sub-assembly may have revisions of its own, so the tree is
              only true AS AT a date. GHERP read an effective date and then
              never used it (ledger D33). */}
          <label className="inline-field">
            As at{' '}
            <input type="date" className="cell" value={asOf || data.as_of}
                   aria-label="Structure as at"
                   onChange={e => setAsOf(e.target.value)} />
          </label>
          {/* Named apart from the header's "Export to Excel", which exports
              this revision's own lines - two buttons, two different
              spreadsheets, so they must not share a name. */}
          {canPrint && (
            <button className="btn btn-sm" onClick={() => void exportStructure()}>
              Export structure
            </button>
          )}
        </div>
      </div>

      <p className="form-help">
        {t.parts.toLocaleString('en-IN')} part{t.parts === 1 ? '' : 's'} in{' '}
        {t.lines.toLocaleString('en-IN')} place{t.lines === 1 ? '' : 's'}, down{' '}
        {t.levels} level{t.levels === 1 ? '' : 's'} — {fmtQty(t.weight_kg)} kg,
        {' '}as at {fmtDMY(data.as_of)}.
      </p>

      {view === 'tree' ? (
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>Level</th><th>Part</th><th>Position</th>
                <th className="num">Qty Each</th><th className="num">Qty Total</th>
                <th>UOM</th><th className="num">Weight (kg)</th><th></th>
              </tr>
            </thead>
            <tbody>
              {visible.map(r => {
                const isParent = parents.has(key(r.path))
                const shut = collapsed.has(key(r.path))
                return (
                  <tr key={key(r.path)} className={r.is_optional ? 'row-muted' : undefined}>
                    <td>{r.level}</td>
                    <td>
                      <span style={{ paddingLeft: (r.level - 1) * 18, display: 'inline-block' }}>
                        {isParent ? (
                          <button className="link-btn" aria-expanded={!shut}
                                  aria-label={`${shut ? 'Expand' : 'Collapse'} ${r.item_id}`}
                                  onClick={() => toggle(r.path)}
                                  style={{ marginRight: 6 }}>{shut ? '▸' : '▾'}</button>
                        ) : <span style={{ display: 'inline-block', width: 18 }} />}
                        {/* A part that is made opens its own BOM — every
                            reference is a door. UI_TASTE.md. */}
                        {r.child_revision !== null
                          ? <button className="link-btn" onClick={() => onOpen(r.item_id)}
                                    title="Open this part’s own BOM">{r.item_id}</button>
                          : r.item_id}{' '}— {r.item_name}
                        {r.child_revision !== null && (
                          <span className="row-muted"> (rev {r.child_revision})</span>
                        )}
                        {!r.item_active && (
                          <span className="badge badge-inactive" style={{ marginLeft: 6 }}>Inactive</span>
                        )}
                      </span>
                    </td>
                    <td>{r.position_mark ?? '—'}</td>
                    <td className="num">{fmtQty(r.qty_each)}</td>
                    <td className="num">{fmtQty(r.qty_total)}</td>
                    <td>{r.uom}</td>
                    <td className="num">
                      {r.exploded ? '' : r.weight_kg == null ? '—' : fmtQty(r.weight_kg)}
                    </td>
                    <td>{r.is_optional ? 'Optional' : ''}</td>
                  </tr>
                )
              })}
              <tr className="total-row">
                <td colSpan={6} className="num">Weight</td>
                <td className="num">{fmtQty(t.weight_kg)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>Part</th><th className="num">Total Qty</th><th>UOM</th>
                <th className="num">Weight (kg)</th><th className="num">Used in</th><th></th>
              </tr>
            </thead>
            <tbody>
              {data.flat.map(f => (
                <tr key={f.item_id} className={f.optional_only ? 'row-muted' : undefined}>
                  <td>
                    {f.exploded
                      ? <button className="link-btn" onClick={() => onOpen(f.item_id)}
                                title="Open this part’s own BOM">{f.item_id}</button>
                      : f.item_id}{' '}— {f.item_name}
                    {!f.item_active && (
                      <span className="badge badge-inactive" style={{ marginLeft: 6 }}>Inactive</span>
                    )}
                  </td>
                  <td className="num">{fmtQty(f.qty_total)}</td>
                  <td>{f.uom}</td>
                  <td className="num">
                    {f.exploded ? '' : f.weight_kg == null ? '—' : fmtQty(f.weight_kg)}
                  </td>
                  <td className="num">{f.places}</td>
                  <td>{f.optional_only ? 'Optional' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="form-help">
        {view === 'flat' && 'Every part below this BOM, at every level, with its ' +
          'total quantity — a sub-assembly shows no weight of its own because its ' +
          'parts are counted instead. '}
        {t.optional > 0 && `${t.optional} line${t.optional > 1 ? 's are' : ' is'} ` +
          'optional and left out of the weight — the customer chooses one of each ' +
          'group, so only one is built. '}
        {t.unweighed.length > 0 && `${t.unweighed.length} part${t.unweighed.length > 1 ? 's have' : ' has'} ` +
          `no weight and ${t.unweighed.length > 1 ? 'are' : 'is'} not in the total: ` +
          `${t.unweighed.slice(0, 12).join(', ')}${t.unweighed.length > 12 ? '…' : ''}. ` +
          'Set the weight per unit on the Items screen. '}
        {t.inactive.length > 0 && `${t.inactive.length} part${t.inactive.length > 1 ? 's are' : ' is'} ` +
          `marked inactive: ${t.inactive.slice(0, 12).join(', ')}${t.inactive.length > 12 ? '…' : ''}. `}
        {t.truncated && 'The tree is deeper than 12 levels and is shown to 12. '}
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Revisions, where-used, compare
// ---------------------------------------------------------------------------

function RevisionsTab({ revisions, selected, onSelect }: {
  revisions: Revision[]; selected: string | null; onSelect: (id: string) => void
}) {
  return (
    <div className="card">
      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th style={{ width: 70 }}>Rev</th><th style={{ width: 120 }}>State</th>
              <th style={{ width: 130 }}>Effective From</th>
              <th className="num" style={{ width: 70 }}>Lines</th>
              <th style={{ width: 180 }}>Released</th>
              <th style={{ width: 110 }}>Copied From</th><th>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {revisions.length === 0 && (
              <tr><td colSpan={7} className="empty">No revisions yet.</td></tr>
            )}
            {revisions.map(v => (
              <tr key={v.bom_id} className="row-openable" title="Double-click to open"
                  onDoubleClick={() => onSelect(v.bom_id)}
                  style={v.bom_id === selected ? { fontWeight: 600 } : undefined}>
                <td><button className="link-btn" onClick={() => onSelect(v.bom_id)}>
                  Rev {v.revision}</button></td>
                <td><span className={`badge badge-${v.state.toLowerCase()}`}>{v.state}</span></td>
                <td>{fmtDMY(v.effective_from)}</td>
                <td className="num">{v.lines}</td>
                <td>{v.released_at
                  ? `${fmtDMY(v.released_at)}${v.released_by ? ` · ${v.released_by}` : ''}` : '—'}</td>
                <td>{v.copied_from_revision ? `Rev ${v.copied_from_revision}` : '—'}</td>
                <td>{v.state === 'Cancelled' ? `Cancelled: ${v.cancel_reason}` : v.remarks ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

interface UsedIn {
  parent_id: string; parent_name: string; parent_category: string
  bom_id: string; revision: number; state: State; effective_from: string | null
  line_no: number; qty: string | number; position_mark: string | null
  is_optional: boolean
}

function WhereUsedTab({ itemId, onOpen }: { itemId: string; onOpen: (id: string) => void }) {
  const [rows, setRows] = useState<UsedIn[] | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  useEffect(() => {
    api.get<{ rows: UsedIn[] }>(`/masters/bom/where-used/${itemId}`)
      .then(r => setRows(r.rows ?? []))
      .catch(e => { setRows([]); setProblem(message(e, 'Where-used could not be loaded.')) })
  }, [itemId])

  return (
    <div className="card">
      {problem && <div className="form-errors">{problem}</div>}
      <p className="form-help" style={{ margin: '0 0 8px' }}>
        Which bills of material use {itemId} as a part. By customer arrives
        with Projects.
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr><th>Used In</th><th style={{ width: 90 }}>Rev</th>
              <th style={{ width: 120 }}>State</th><th style={{ width: 100 }}>Position</th>
              <th className="num" style={{ width: 90 }}>Qty</th>
              <th style={{ width: 80 }}>Optional</th></tr>
          </thead>
          <tbody>
            {rows === null && <tr><td colSpan={6} className="loading">Loading…</td></tr>}
            {rows?.length === 0 && (
              <tr><td colSpan={6} className="empty">
                {itemId} is not a part in any bill of material.
              </td></tr>
            )}
            {rows?.map(r => (
              <tr key={`${r.bom_id}:${r.line_no}`}>
                <td><button className="link-btn" onClick={() => onOpen(r.parent_id)}>
                  {r.parent_id}</button> — {r.parent_name}</td>
                <td>Rev {r.revision}</td>
                <td><span className={`badge badge-${r.state.toLowerCase()}`}>{r.state}</span></td>
                <td>{r.position_mark ?? '—'}</td>
                <td className="num">{fmtQty(r.qty)}</td>
                <td>{r.is_optional ? 'Optional' : ''}</td>
              </tr>
            ))}
            {!!rows?.length && (
              <tr className="total-row"><td colSpan={6} className="num">
                Total: {rows.length} use{rows.length === 1 ? '' : 's'}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

interface Diff {
  item_id: string; item_name: string; position_mark: string | null
  change: 'Added' | 'Removed' | 'Changed' | 'Same'
  qty_a: string | number | null; qty_b: string | number | null
  optional_a: boolean | null; optional_b: boolean | null
}
const CHANGE_BADGE = { Added: 'badge-authorised', Removed: 'badge-cancelled',
                       Changed: 'badge-submitted', Same: 'badge-draft' }

function CompareTab({ revisions, selected }: {
  revisions: Revision[]; selected: string | null
}) {
  const usable = revisions.filter(v => v.state !== 'Cancelled')
  const idx = usable.findIndex(v => v.bom_id === selected)
  // Default: the previous revision against the one being looked at.
  const [a, setA] = useState(usable[idx + 1]?.bom_id ?? '')
  const [b, setB] = useState(selected ?? '')
  const [rows, setRows] = useState<Diff[] | null>(null)
  const [sameToo, setSameToo] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  useEffect(() => {
    if (!a || !b || a === b) { setRows(null); return }
    api.get<{ rows: Diff[] }>('/masters/bom/compare', { a, b })
      .then(r => { setRows(r.rows); setProblem(null) })
      .catch(e => setProblem(message(e, 'The comparison could not be made.')))
  }, [a, b])

  const options: Option[] = usable.map(v => ({
    value: v.bom_id, label: `Rev ${v.revision} — ${v.state}` }))
  const shown = (rows ?? []).filter(r => sameToo || r.change !== 'Same')
  const label = (id: string) => usable.find(v => v.bom_id === id)?.revision

  return (
    <div className="card">
      {problem && <div className="form-errors">{problem}</div>}
      {usable.length < 2 ? (
        <div className="empty">
          There is only one revision, so there is nothing to compare it with yet.
        </div>
      ) : (<>
        <div className="toolbar">
          <div style={{ minWidth: 200 }}><SearchSelect value={a} onChange={setA}
            options={options} emptyLabel="— earlier revision —" /></div>
          <span>against</span>
          <div style={{ minWidth: 200 }}><SearchSelect value={b} onChange={setB}
            options={options} emptyLabel="— later revision —" /></div>
          <div className="grow" />
          <label className="check-row">
            <input type="checkbox" checked={sameToo}
                   onChange={e => setSameToo(e.target.checked)} />
            Show unchanged lines
          </label>
        </div>
        {a === b && a && <div className="empty">Choose two different revisions.</div>}
        {rows && a !== b && (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr><th>Part</th><th style={{ width: 100 }}>Position</th>
                  <th style={{ width: 110 }}>Change</th>
                  <th className="num" style={{ width: 110 }}>Qty in Rev {label(a)}</th>
                  <th className="num" style={{ width: 110 }}>Qty in Rev {label(b)}</th></tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr><td colSpan={5} className="empty">
                    No differences — the two revisions have the same lines.
                  </td></tr>
                )}
                {shown.map(r => (
                  <tr key={`${r.item_id}:${r.position_mark ?? ''}`}>
                    <td>{r.item_id} — {r.item_name}</td>
                    <td>{r.position_mark ?? '—'}</td>
                    <td><span className={`badge ${CHANGE_BADGE[r.change]}`}>{r.change}</span>
                      {r.change === 'Changed' && r.optional_a !== r.optional_b &&
                        <span className="row-muted"> · optional {r.optional_b ? 'added' : 'removed'}</span>}</td>
                    <td className="num">{fmtQty(r.qty_a)}</td>
                    <td className="num">{fmtQty(r.qty_b)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>)}
    </div>
  )
}
